import React from "react";

/**
 * Phase 14.8 — root-level ErrorBoundary.
 *
 * Wraps the entire route tree so any uncaught render-time throw
 * (e.g. `localStorage` SecurityError in incognito, undefined prop
 * access in a card renderer, malformed API payload, …) shows a
 * graceful "Reload" UI instead of a blank white screen.
 *
 * Phase 14.8.3 — inline error details. Production crash (incognito,
 * React #31 from ItineraryCard) was diagnosed via DevTools console
 * only; users without DevTools open had no copy-pasteable error to
 * share. We now render the captured `error.message` and a short
 * component-stack excerpt INLINE behind a "Show technical details"
 * toggle so future incidents are self-diagnosing.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, componentStack: null, showDetails: false };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidCatch(error, info) {
        // Capture component stack into state so the fallback UI can
        // render it; also log for any DevTools-connected viewer.
        const componentStack = (info && info.componentStack) || null;
        this.setState({ componentStack });
        console.error("[ErrorBoundary] crash:", error, componentStack);
    }
    handleReload = () => {
        try { window.location.reload(); } catch { /* noop */ }
    };
    handleToggleDetails = () => {
        this.setState((s) => ({ showDetails: !s.showDetails }));
    };
    render() {
        if (!this.state.error) return this.props.children;
        const { error, componentStack, showDetails } = this.state;
        // Force string — `error` can be anything thrown (Error, string,
        // object), and `String(err)` is the React-safest read.
        const errMsg = String(error && (error.message || error)) || "Unknown error";
        const stackExcerpt = String(error && error.stack ? error.stack : "")
            .split("\n").slice(0, 6).join("\n");
        const compStack = (componentStack || "").split("\n").slice(0, 8).join("\n");
        return (
            <div
                role="alert"
                data-testid="root-error-boundary"
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "24px",
                    fontFamily: "Inter, system-ui, sans-serif",
                    color: "#0A0A0A",
                    background: "#FFFFFF",
                    textAlign: "center",
                }}
            >
                <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>
                    Something went wrong.
                </h1>
                <p style={{ marginTop: 8, color: "#737373", fontSize: 14, maxWidth: 480 }}>
                    Piqpath hit an unexpected error rendering this page. Reloading
                    usually fixes it. If it keeps happening, drop us a note at{" "}
                    <a
                        href="mailto:connect@piqpath.com"
                        style={{ color: "#0A0A0A", textDecoration: "underline" }}
                    >
                        connect@piqpath.com
                    </a>{" "}
                    and include the technical details below.
                </p>
                <button
                    type="button"
                    onClick={this.handleReload}
                    data-testid="root-error-reload"
                    style={{
                        marginTop: 20,
                        background: "#0A0A0A",
                        color: "#FFFFFF",
                        border: "none",
                        borderRadius: 8,
                        padding: "10px 18px",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                    }}
                >
                    Reload
                </button>
                <button
                    type="button"
                    onClick={this.handleToggleDetails}
                    data-testid="root-error-toggle-details"
                    aria-expanded={showDetails}
                    style={{
                        marginTop: 14,
                        background: "transparent",
                        color: "#525252",
                        border: "none",
                        padding: "4px 6px",
                        fontSize: 12,
                        textDecoration: "underline",
                        cursor: "pointer",
                    }}
                >
                    {showDetails ? "Hide technical details" : "Show technical details"}
                </button>
                {showDetails && (
                    <pre
                        data-testid="root-error-details"
                        style={{
                            marginTop: 12,
                            maxWidth: 720,
                            width: "100%",
                            maxHeight: 280,
                            overflow: "auto",
                            background: "#F5F5F5",
                            color: "#0A0A0A",
                            border: "1px solid #E5E5E5",
                            borderRadius: 8,
                            padding: "12px 14px",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            fontSize: 11,
                            lineHeight: 1.5,
                            textAlign: "left",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        {`Error: ${errMsg}`}
                        {stackExcerpt ? `\n\n${stackExcerpt}` : ""}
                        {compStack ? `\n\nComponent stack:${compStack}` : ""}
                    </pre>
                )}
            </div>
        );
    }
}
